import {
    ChangeDetectionStrategy,
    Component,
    input,
    output,
    viewChildren,
    effect,
    computed,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterLink} from '@angular/router';
import {DateTime} from 'luxon';
import {
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import {
    AddressComponent,
    applySortingToHeaders,
    SortableTableHeaderComponent,
    Sorting,
    DatePipe,
    TrueFalseComponent,
} from '@brunata-angular/ui';
import {TranslatePipe} from '@brunata-angular/util';
import {LocationWithDetails} from '@shared/services/location.service';
import {
    canInviteConsumer,
    ConsumerRowConsumer,
    consumerRowsInDisplayOrder,
    EnhancedLocationsConsumer,
    getInviteLabelKey,
    GroupedLocation,
    hasHiddenHistory,
    RowKind,
} from '../resident-rows';

type RenderRow = {
    consumer: ConsumerRowConsumer | null;
    location: LocationWithDetails['location'];
    proBraLoc: string;
    isFirstInLocation: boolean;
    locationRowspan: number;
    striped: boolean;
    kind: RowKind;
    canInvite: boolean;
    inviteLabelKey: string;
    canToggleHistory: boolean;
    historyLabelKey: string;
};

export type ConsumerRowSelection = {
    locationId: string;
    consumer: EnhancedLocationsConsumer;
};

export type ConsumerInvite = {
    locationId: string;
    consumerId: string;
    email: string | null;
    labelKey: string;
};

@Component({
    selector: 'app-residents-table',
    standalone: true,
    imports: [
        CommonModule,
        AddressComponent,
        SortableTableHeaderComponent,
        TranslatePipe,
        DatePipe,
        TrueFalseComponent,
        RouterLink,
        NgbDropdown,
        NgbDropdownToggle,
        NgbDropdownMenu,
        NgbDropdownItem,
    ],
    templateUrl: './residents-table.component.html',
    styleUrl: './residents-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResidentsTableComponent {
    readonly groupedLocations = input<GroupedLocation[]>([]);
    readonly page = input(1);
    readonly pageSize = input(20);
    readonly currentSorting = input<Sorting>({column: 'propertyNo', direction: 'asc'});
    readonly showPast = input<boolean>(false);
    readonly showFuture = input<boolean>(false);
    readonly showOnlineAccess = input<boolean>(false);
    readonly inviteInFlight = input<boolean>(false);
    readonly expandedLocationIds = input<ReadonlySet<string>>(new Set());

    readonly sort = output<Sorting>();
    readonly inviteConsumer = output<ConsumerInvite>();
    readonly editConsumer = output<ConsumerRowSelection>();
    readonly toggleLocationHistory = output<string>();

    readonly columnCount = computed(() => (this.showOnlineAccess() ? 8 : 7));

    private readonly headers = viewChildren(SortableTableHeaderComponent);

    readonly preparedRows = computed<RenderRow[]>(() => {
        const locations = this.groupedLocations();
        const showOnlineAccess = this.showOnlineAccess();
        const includePast = this.showPast();
        const includeFuture = this.showFuture();
        const expandedLocationIds = this.expandedLocationIds();
        const today = DateTime.now().startOf('day');
        const startIdx = (this.page() - 1) * this.pageSize();
        const endIdx = this.page() * this.pageSize();

        const rows: RenderRow[] = [];

        locations.slice(startIdx, endIdx).forEach((group, locIndex) => {
            const loc = group.location;
            const proBraLoc = `${loc.propertyNo ?? ''}-${loc.branchNo ?? ''}-${
                loc.locationNo ?? ''
            }`;
            const striped = locIndex % 2 === 1;
            const expanded = expandedLocationIds.has(loc.locationId);
            const canToggleHistory = hasHiddenHistory(group, includePast, includeFuture, today);

            const orderedConsumers = consumerRowsInDisplayOrder(
                group,
                includePast || expanded,
                includeFuture || expanded,
                today,
            );
            const rowspan = orderedConsumers.length;
            orderedConsumers.forEach((row, index) => {
                rows.push({
                    consumer: row.consumer,
                    location: loc,
                    proBraLoc,
                    isFirstInLocation: index === 0,
                    locationRowspan: rowspan,
                    striped,
                    kind: row.kind,
                    canInvite:
                        showOnlineAccess &&
                        row.consumer != null &&
                        canInviteConsumer(row.consumer, row.consumer.isCurrent, loc.virtual),
                    inviteLabelKey: getInviteLabelKey(row.consumer),
                    canToggleHistory,
                    historyLabelKey: expanded
                        ? 'actions_hideConsumerHistory'
                        : 'actions_showConsumerHistory',
                });
            });
        });

        return rows;
    });

    constructor() {
        effect(() => {
            applySortingToHeaders(this.headers(), this.currentSorting());
        });
    }

    onSort(sorting: Sorting): void {
        applySortingToHeaders(this.headers(), sorting);
        this.sort.emit(sorting);
    }

    protected onEditConsumer(row: RenderRow): void {
        const consumer = row.consumer;
        if (consumer == null) {
            return;
        }
        this.editConsumer.emit({locationId: row.location.locationId, consumer});
    }

    protected onToggleHistory(row: RenderRow): void {
        this.toggleLocationHistory.emit(row.location.locationId);
    }

    protected onInviteConsumer(row: RenderRow): void {
        const consumer = row.consumer;
        if (consumer == null) {
            return;
        }
        this.inviteConsumer.emit({
            locationId: row.location.locationId,
            consumerId: consumer.consumerId,
            email: consumer.email,
            labelKey: row.inviteLabelKey,
        });
    }
}
